// PowerSync `useQuery` (@powersync/react) отдаёт
// `{ data, isLoading, isFetching, error, refresh }`. Существующие UI-компоненты
// (написанные под TanStack Query / tRPC) ждут `{ isError, refetch }`.
//
// Этот shim приводит результат PowerSync-запроса к ожидаемому компонентами
// контракту БЕЗ повторного fetch — `data` уже смаплен вызывающим хуком.

import { useEffect, useState } from "react";

/**
 * Потолок ожидания ПЕРВОГО результата запроса, отсчитывается от монтирования.
 *
 * Раньше здесь стоял `usePowerSyncReady()` — флаг «первичный синк прошёл».
 * Он решал ту же задачу (не висеть в скелетоне вечно, если инициализация
 * локальной SQLite зависла), но ломал честность загрузки: после первого синка
 * флаг синхронно стартует в `true` на КАЖДОМ монтировании, поэтому shim отдавал
 * `isLoading: false` ещё до того, как SQLite вернула строки. Экран получал
 * «загрузка кончилась, данных нет» и на долю секунды рисовал пустое состояние —
 * на главной это был онбординг-герой «Начнём с первого материала?» человеку с
 * полной библиотекой, при каждом возврате на вкладку.
 *
 * Потолок от монтирования держит обе гарантии: пока запрос честно грузится —
 * это загрузка, а зависшая БД всё так же не оставляет экран в скелетоне навсегда.
 */
export const FIRST_RESULT_TIMEOUT_MS = 7000;

export interface PowerSyncQueryLike {
	isLoading: boolean;
	error?: unknown;
	refresh?: () => Promise<unknown>;
}

export interface ShimmedQuery<TData> {
	data: TData;
	isLoading: boolean;
	isError: boolean;
	error: unknown;
	refetch: () => Promise<unknown>;
}

/**
 * `true`, если `active` держится дольше {@link FIRST_RESULT_TIMEOUT_MS}.
 * Сбрасывается, как только `active` снялся, — запрос с изменившимися
 * параметрами получает свой отсчёт заново.
 */
function useStalled(active: boolean, timeoutMs: number = FIRST_RESULT_TIMEOUT_MS): boolean {
	const [stalled, setStalled] = useState(false);

	useEffect(() => {
		if (!active) {
			setStalled(false);
			return;
		}
		const timer = setTimeout(() => setStalled(true), timeoutMs);
		return () => clearTimeout(timer);
	}, [active, timeoutMs]);

	return stalled;
}

/**
 * Тот же гейт, что внутри `usePowerSyncQueryShim`, но отдельно — для хуков,
 * которые отдают наружу свой контракт (`{ items, isLoading }`) и заворачивать
 * их в shim значило бы менять контракт всем потребителям.
 *
 * Без гейта `isLoading` PowerSync-запроса НИКОГДА не станет false, если
 * инициализация локальной SQLite (WASM/OPFS/worker) зависла или упала — экран
 * остаётся в скелетоне навсегда.
 */
export function usePowerSyncLoading(isLoading: boolean): boolean {
	const stalled = useStalled(isLoading);
	return isLoading && !stalled;
}

export function usePowerSyncQueryShim<TData>(
	q: PowerSyncQueryLike,
	data: TData,
): ShimmedQuery<TData> {
	const isLoading = usePowerSyncLoading(q.isLoading);
	return {
		data,
		isLoading,
		isError: q.error != null,
		error: q.error ?? null,
		refetch: () => q.refresh?.() ?? Promise.resolve(),
	};
}
